import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {ResultEntry} from '../../../../document-view-prizmdoc/hooks/usePrizmDoc';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './PersistentHighlight.scss';
import ArrIconButton from '../../../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as ChevronIcon} from '../../../../../assets/images/chevron-base-icon.svg';
import {ArrToggle} from '../../../../common/arr-toggle/ArrToggle';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrModal from '../../../../common/arr-modal/ArrModal';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';

interface PersistentHighlightWrapperProps {
  highlightOptions?: Record<string, ResultEntry>;
  onNavigateResult?: (result: SearchResult | null) => void;
  emptyConcepts: {description: string; color: string}[];
  disableNavigation?: boolean;
}

interface NavSelection {
  selectedTerm: string;
  selectedIndex: number;
}

const HIT_TEXT_LIMIT = 90;

const HighlightItem = ({
  index,
  result,
  selected,
  setSelected,
  onViewAll,
  disableNavigation,
}: {
  index: number;
  result: ResultEntry;
  selected?: NavSelection;
  setSelected?: Dispatch<SetStateAction<NavSelection | undefined>>;
  onViewAll?: (text: string) => void;
  disableNavigation?: boolean;
}) => {
  const textRef = useRef<HTMLElement>(null);

  const isCurrentGroupHighlighted = useMemo(
    () => selected?.selectedTerm === result.actualSearchString,
    [selected, result]
  );
  const prevDisabled = useMemo(() => {
    if (disableNavigation || result.hits.length === 0) return true;
    if (isCurrentGroupHighlighted) {
      return selected?.selectedIndex === 0;
    } else {
      return true;
    }
  }, [disableNavigation, isCurrentGroupHighlighted, selected]);

  const nextDisabled = useMemo(() => {
    if (disableNavigation || result.hits.length === 0) return true;

    if (isCurrentGroupHighlighted) {
      return selected?.selectedIndex === Number(result.count);
    } else {
      return false;
    }
  }, [disableNavigation, isCurrentGroupHighlighted, selected]);

  const onPreviousClick = useCallback(() => {
    if (selected?.selectedIndex === 1) {
      setSelected?.(undefined);
    } else {
      setSelected?.({
        selectedTerm: result.actualSearchString,
        selectedIndex:
          selected?.selectedTerm === result.actualSearchString
            ? selected.selectedIndex - 1
            : 0,
      });
    }
  }, [setSelected, selected, result]);

  const onNextClick = useCallback(() => {
    setSelected?.({
      selectedTerm: result.actualSearchString,
      selectedIndex:
        selected?.selectedTerm === result.actualSearchString
          ? selected.selectedIndex + 1
          : 1,
    });
  }, [selected, result]);

  return (
    <Box
      className="highlight-result"
      sx={{
        borderLeftColor: `#${result.colorCode.replace('#', '')} !important`,
      }}
    >
      <Box className="count-wrapper">
        <Typography
          className="typo-text label3_size search-bar-label"
          data-testid={`persistent-highlight-wrapper-result-type-${index}`}
        >
          {result.type} :
        </Typography>
        <Typography
          className="typo-text label3_size search-bar-label"
          data-testid={`hit-count-${index}`}
        >
          {t('docView.persistentSearch.hitCount').replace(
            '$hitCount',
            `${result.hits.length}`
          )}
        </Typography>
      </Box>
      <Box className="text-wrapper">
        <Typography
          className="typo-text body4_size search-bar-label term-text"
          component={'span'}
          ref={textRef}
          data-testid={`hit-name-${index}`}
        >
          {result.name.length > HIT_TEXT_LIMIT
            ? `${result.name.slice(0, HIT_TEXT_LIMIT)}...`
            : result.name}
          {result.name.length > HIT_TEXT_LIMIT && (
            <ArrButton
              variant="text"
              size="xs"
              sx={{display: 'inline'}}
              onClick={() => {
                onViewAll?.(result.name);
              }}
              dataTestId={`${index}-view-all`}
            >
              <Typography
                className="typo-text buttonXS_size "
                data-testid={`persistent-highlight-wrapper-view-all`}
              >
                {t('docView.persistentSearch.viewAll')}
              </Typography>
            </ArrButton>
          )}
        </Typography>
      </Box>
      <Box className="nav-wrapper" gap={1}>
        <ArrIconButton
          testId={`${index}-previous`}
          className="previous-button"
          size="small"
          icon={<ChevronIcon className="previous-icon" />}
          ariaLabel={t('docView.docPagination.previous')}
          onClick={onPreviousClick}
          disabled={prevDisabled}
          shape={prevDisabled ? 'none' : 'default'}
        />
        <ArrIconButton
          testId={`${index}-next`}
          className="next-button"
          size="small"
          icon={<ChevronIcon className="next-icon" />}
          ariaLabel={t('docView.docPagination.next')}
          onClick={onNextClick}
          disabled={nextDisabled}
          shape={nextDisabled ? 'none' : 'default'}
        />
      </Box>
    </Box>
  );
};

const PersistentHighlightWrapper: React.FC<PersistentHighlightWrapperProps> = ({
  highlightOptions,
  onNavigateResult,
  emptyConcepts,
  disableNavigation,
}) => {
  const [selected, setSelected] = useState<NavSelection>();
  const [modalText, setModalText] = useState('');
  const userId = useSelector(
    (state: RootState) => state.authenticatedUser.user.userId
  );
  const [hideZeroHits, setHideZeroHits] = useState(
    localStorage.getItem(`${userId}-hideHitCount`) === 'true'
  );

  const handleToggleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setHideZeroHits(checked);
      localStorage.setItem(`${userId}-hideHitCount`, JSON.stringify(checked));
    },
    [setHideZeroHits, userId]
  );

  useEffect(() => {
    // No search has completed yet, so the viewer has no search state to navigate.
    if (!highlightOptions) return;
    const term = selected
      ? (highlightOptions?.[selected.selectedTerm]?.hits[
          selected.selectedIndex - 1
        ] ?? null)
      : null;
    onNavigateResult && onNavigateResult(term);
  }, [selected, highlightOptions]);

  const filteredHits = useMemo(() => {
    const hits = [
      ...(highlightOptions ? Object.values(highlightOptions) : []),
      ...emptyConcepts.map((concept) => ({
        colorCode: concept.color,
        count: 0,
        hits: [],
        name: concept.description,
        actualSearchString: concept.description,
        type: 'Concept',
      })),
    ];
    return hideZeroHits ? hits.filter((hit) => hit.count !== 0) : hits;
  }, [hideZeroHits, highlightOptions, emptyConcepts]);

  const isEmptyState = useMemo(
    () => !highlightOptions || Object.values(highlightOptions).length === 0,
    [highlightOptions]
  );

  const closeModal = () => {
    setModalText('');
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      data-testid="persistent-navigation-wrapper"
      className="persistent-search-wrapper"
    >
      <ArrModal
        headerTitle={'Title'}
        headerTitleClassName="headline3_size view-all-modal-heading-persistent-search"
        showModal={!!modalText}
        showClose={true}
        onClose={closeModal}
        className="persistent-search-modal"
        description={modalText}
        dataTestId="persistent-search-modal"
      ></ArrModal>
      <Box className="search-hits-header">
        <Typography
          className="typo-text subhead5_size title"
          data-testid={`persistent-highlight-wrapper-title`}
        >
          {t('docView.persistentSearch.title')}
        </Typography>
        {!isEmptyState && (
          <Box className="hits-box">
            <Typography
              className="typo-text body4_size search-bar-label"
              data-testid={`persistent-highlight-wrapper-hits-disable`}
            >
              {t('docView.persistentSearch.hitsDisable')}
            </Typography>
            <ArrToggle
              disabled={false}
              checked={hideZeroHits}
              toggleSize="large"
              onChange={handleToggleChange}
              dataTestId={`toggle-hit-count`}
            />
          </Box>
        )}
      </Box>
      {filteredHits.map((term, index) => (
        <HighlightItem
          index={index}
          key={`${term.name}${index}`}
          result={term}
          selected={selected}
          setSelected={setSelected}
          disableNavigation={disableNavigation}
          onViewAll={(text: string) => {
            setModalText(text);
          }}
        />
      ))}
    </Box>
  );
};

export default PersistentHighlightWrapper;
